import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Inbox } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useUpdateRequests } from '../../../hooks/useEqmsDocuments';
import type { EqmsUpdateRequest } from '../../../types/eqms.types';
import { UpdateRequestCard } from './UpdateRequestCard';

const FILTERS = ['open', 'addressing', 'parked', 'closed'] as const;
type Filter = (typeof FILTERS)[number];

const matches = (filter: Filter, request: EqmsUpdateRequest) =>
  filter === 'closed' ? ['declined', 'done'].includes(request.status) : request.status === filter;

/**
 * The owner's standing list of update requests. It stays on the Command Center
 * while anything is pending so parked requests are not forgotten.
 */
export function UpdateRequestsInbox({ onOpenDocument }: { onOpenDocument: (documentId: string) => void }) {
  const { t } = useTranslation('qms');
  const query = useUpdateRequests({ scope: 'owner' });
  const [filter, setFilter] = useState<Filter>('open');
  const requests = useMemo(() => query.data?.requests ?? [], [query.data]);
  const counts = query.data?.counts;
  const closedCount = requests.filter((request) => matches('closed', request)).length;

  const groups = useMemo(() => {
    const byDocument = new Map<string, EqmsUpdateRequest[]>();
    for (const request of requests.filter((item) => matches(filter, item))) {
      byDocument.set(request.documentId, [...(byDocument.get(request.documentId) ?? []), request]);
    }
    return [...byDocument.values()];
  }, [filter, requests]);

  if (query.isLoading) return <Skeleton className="mb-6 h-32 w-full" />;
  if (query.isError) {
    return (
      <div role="alert" className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        <div>
          <p className="font-medium">{t('updateRequests.loadFailed')}</p>
          <p className="mt-1">{t('updateRequests.loadFailedHint')}</p>
        </div>
        <Button size="sm" variant="outline" disabled={query.isFetching} onClick={() => { void query.refetch(); }}>{t('authoring.retry')}</Button>
      </div>
    );
  }
  if (!requests.length) return null;

  const filterCount = (value: Filter) => (value === 'closed' ? closedCount : counts?.[value] ?? 0);

  return (
    <Card id="eqms-update-requests" className="mb-6 border-amber-200 shadow-sm">
      <CardContent className="p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-amber-50 p-2.5 text-amber-700"><Inbox className="h-5 w-5" /></div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{t('updateRequests.inboxTitle')}</h2>
              <p className="mt-1 max-w-3xl text-sm text-slate-500">{t('updateRequests.inboxDescription')}</p>
            </div>
          </div>
          <div role="tablist" aria-label={t('updateRequests.tab')} className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1">
            {FILTERS.map((value) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={filter === value}
                onClick={() => setFilter(value)}
                className={cn('rounded-md px-3 py-1.5 text-sm text-slate-600 transition',
                  filter === value && 'bg-white font-medium text-slate-900 shadow-sm')}
              >
                {t(`updateRequests.filters.${value}`)}
                <span className={cn('ml-1.5 rounded-full px-1.5 text-xs',
                  value === 'open' && filterCount(value) > 0 ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-600')}>
                  {filterCount(value)}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 space-y-5">
          {groups.length === 0 && <p className="py-4 text-center text-sm text-slate-500">{t('updateRequests.emptyFilter')}</p>}
          {groups.map((group) => {
            const { document } = group[0];
            return (
              <section key={document.id} aria-label={document.name} className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <span className="font-mono text-xs font-semibold text-slate-900">{document.controlCode}</span>
                    <span className="ml-2 break-words text-sm text-slate-700">{document.name}</span>
                    {document.effectiveControlledVersion && (
                      <span className="ml-2 font-mono text-xs text-slate-400">v{document.effectiveControlledVersion}</span>
                    )}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => onOpenDocument(document.id)}>{t('updateRequests.actions.openDocument')}</Button>
                </div>
                {group.map((request) => <UpdateRequestCard key={request.id} request={request} mode="owner" />)}
              </section>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
